import { ProgressPersistence } from "@treefort/lib/progress-persistence"

import { progressStore } from "../../watermelon/stores/progress"
import { MILLISECONDS_FROM_END_TO_COUNT_AS_FINISHED } from "../av/constants"
import { Track } from "../av/types"
import {
  ConsumableContent,
  AudiobookConsumableContent,
  EbookConsumableContent,
  PodcastEpisodeConsumableContent,
  VideoConsumableContent,
  AlbumConsumableContent,
} from "../consumable-content"
import { getProgressItemKey } from "./base"
import {
  AudiobookProgressItem,
  PlayableProgressItem,
  PlayableProgressItemData,
  PodcastEpisodeProgressItem,
  VideoProgressItem,
  AudiobookProgressItemData,
  VideoProgressItemData,
  PodcastEpisodeProgressItemData,
  AlbumProgressItem,
  AlbumProgressItemData,
} from "./playable"
import {
  EbookProgressItem,
  EbookProgressItemData,
  ReadableProgressItem,
  ReadableProgressItemData,
  WebEmbedProgressItem,
  WebEmbedProgressItemData,
} from "./readable"

export type ProgressItemDataForConsumableContent<T extends ConsumableContent> =
  T extends PodcastEpisodeConsumableContent
    ? PodcastEpisodeProgressItemData
    : Extract<ProgressItemData, { contentType: T["type"] }>

export type ProgressItem = PlayableProgressItem | ReadableProgressItem

export type ProgressItemData =
  | PlayableProgressItemData
  | ReadableProgressItemData

export type ProgressItemForConsumableContent<T extends ConsumableContent> =
  T extends AudiobookConsumableContent
    ? AudiobookProgressItem
    : T extends EbookConsumableContent
      ? EbookProgressItem
      : T extends VideoConsumableContent
        ? VideoProgressItem
        : T extends PodcastEpisodeConsumableContent
          ? PodcastEpisodeProgressItem
          : T extends AlbumConsumableContent
            ? AlbumProgressItem
            : ProgressItem

export type ProgressItemForData<T extends ProgressItemData> =
  T extends AudiobookProgressItemData
    ? AudiobookProgressItem
    : T extends EbookProgressItemData
      ? EbookProgressItem
      : T extends VideoProgressItemData
        ? VideoProgressItem
        : T extends PodcastEpisodeProgressItemData
          ? PodcastEpisodeProgressItem
          : T extends WebEmbedProgressItemData
            ? WebEmbedProgressItem
            : T extends AlbumProgressItemData
              ? AlbumProgressItem
              : ProgressItem

export const progressPersistence = new ProgressPersistence(progressStore)

export function getProgressItemFromConsumableContent<
  T extends ConsumableContent,
>({
  consumableContent,
  profileId,
}: {
  consumableContent: T
  profileId: string | null
}): ProgressItemForConsumableContent<T> {
  switch (consumableContent.type) {
    case "book":
      return new AudiobookProgressItem({
        data: {
          contentId: consumableContent.content.id,
          contentType: consumableContent.content.type,
        },
        profileId,
      }) as ProgressItemForConsumableContent<T>
    case "ebook":
      return new EbookProgressItem({
        data: {
          contentId: consumableContent.content.id,
          contentType: consumableContent.content.type,
        },
        profileId,
      }) as ProgressItemForConsumableContent<T>

    case "webEmbed":
      return new WebEmbedProgressItem({
        data: {
          contentId: consumableContent.content.id,
          contentType: consumableContent.content.type,
        },
        profileId,
      }) as ProgressItemForConsumableContent<T>
    case "video":
      return new VideoProgressItem({
        data: {
          contentId: consumableContent.content.id,
          contentType: consumableContent.content.type,
          playMode: "watch",
        },
        profileId,
      }) as ProgressItemForConsumableContent<T>
    case "podcastEpisode":
      return new PodcastEpisodeProgressItem({
        data: {
          contentId: consumableContent.content.id,
          contentType: consumableContent.content.type,
          podcastEpisode: consumableContent.podcastEpisode.episode,
        },
        profileId,
      }) as ProgressItemForConsumableContent<T>
    case "album":
      return new AlbumProgressItem({
        data: {
          contentId: consumableContent.content.id,
          contentType: consumableContent.content.type,
        },
        profileId,
      }) as ProgressItemForConsumableContent<T>
    default: {
      consumableContent satisfies never
      throw new Error("Unexpected consumable content type")
    }
  }
}

export function getProgressItemFromTrack({
  track,
  position,
  duration,
  playbackRate,
  playMode,
}: {
  track: Track
  position: number
  duration: number
  playbackRate: number
  playMode: "watch" | "listen"
}) {
  const finished =
    duration - position <= MILLISECONDS_FROM_END_TO_COUNT_AS_FINISHED
  switch (track.extra.contentType) {
    case "book":
      return new AudiobookProgressItem({
        data: {
          contentId: track.extra.contentId,
          contentType: "book",
          progress: {
            position,
            index: track.extra.chapterIndex,
            finished: track.extra.lastChapter ? finished : false,
          },
          playbackRate,
        },
        profileId: track.extra.profileId,
      })
    case "video":
      return new VideoProgressItem({
        data: {
          contentId: track.extra.contentId,
          contentType: "video",
          progress: { position, index: 0, finished },
          playbackRate,
          playMode,
        },
        profileId: track.extra.profileId,
      })
    case "podcast":
      return new PodcastEpisodeProgressItem({
        data: {
          contentId: track.extra.contentId,
          contentType: "podcast",
          progress: { position, index: 0, finished },
          playbackRate,
          podcastEpisode: track.extra.podcastEpisodeNumber,
          podcastEpisodeDuration: duration,
        },
        profileId: track.extra.profileId,
      })
    case "album":
      return new AlbumProgressItem({
        data: {
          contentId: track.extra.contentId,
          contentType: "album",
          progress: {
            position,
            index: track.extra.trackIndex,
            finished: false,
          },
          playbackRate,
        },
        profileId: track.extra.profileId,
      })
    default: {
      track.extra satisfies never
      throw new Error("Unexpected content type")
    }
  }
}

export function getProgressItemFromProgressData<T extends ProgressItemData>({
  data,
  profileId,
}: {
  data: T
  profileId: string | null
}) {
  switch (data.contentType) {
    case "book":
      return new AudiobookProgressItem({
        data,
        profileId,
      }) as ProgressItemForData<T>
    case "ebook":
      return new EbookProgressItem({
        data,
        profileId,
      }) as ProgressItemForData<T>
    case "video":
      return new VideoProgressItem({
        data,
        profileId,
      }) as ProgressItemForData<T>
    case "podcast":
      return new PodcastEpisodeProgressItem({
        data,
        profileId,
      }) as ProgressItemForData<T>
    case "webEmbed":
      return new WebEmbedProgressItem({
        data,
        profileId,
      }) as ProgressItemForData<T>
    case "album":
      return new AlbumProgressItem({
        data,
        profileId,
      }) as ProgressItemForData<T>
    default: {
      data satisfies never
      throw new Error("Unexpected content type")
    }
  }
}

export function getFinishedProgressItem<T extends ProgressItem>(
  progressItem: T,
) {
  const profileId = progressItem.getProfileId()

  if (progressItem instanceof AudiobookProgressItem) {
    const data = getFinishedProgressItemData(progressItem.getData())
    return new AudiobookProgressItem({ data, profileId })
  }

  if (progressItem instanceof EbookProgressItem) {
    const data = getFinishedProgressItemData(progressItem.getData())
    return new EbookProgressItem({ data, profileId })
  }

  if (progressItem instanceof VideoProgressItem) {
    const data = getFinishedProgressItemData(progressItem.getData())
    return new VideoProgressItem({ data, profileId })
  }

  if (progressItem instanceof PodcastEpisodeProgressItem) {
    const data = getFinishedProgressItemData(progressItem.getData())
    return new PodcastEpisodeProgressItem({ data, profileId })
  }

  return progressItem
}

function getFinishedProgressItemData<T extends ProgressItemData>(data: T): T {
  switch (data.contentType) {
    case "podcast":
    case "video":
    case "book":
      return {
        ...data,
        progress: { index: 0, position: 0, ...data.progress, finished: true },
      }
    case "ebook":
      return { ...data, progress: { ...data.progress, finished: true } }
    default:
      return data
  }
}

export async function fetchProgress<T extends ProgressItem>({
  key,
  profileId,
}: {
  key: string
  profileId: string | null
}) {
  const { value: data } = await progressPersistence.getLocal<ProgressItemData>(
    key,
    { profileId },
  )
  return data
    ? (getProgressItemFromProgressData({ data, profileId }) as T) || null
    : null
}

export async function fetchProgressForConsumableContent<
  T extends ConsumableContent,
>({
  consumableContent,
  profileId,
}: {
  consumableContent: T
  profileId: string | null
}) {
  const key = getProgressItemKeyFromConsumableContent(consumableContent)
  const progressItem = await fetchProgress<ProgressItemForConsumableContent<T>>(
    { key, profileId },
  )
  return (
    progressItem ||
    getProgressItemFromConsumableContent({ consumableContent, profileId })
  )
}

export async function persistProgress(progressItem: ProgressItem) {
  const key = progressItem.getKey()
  const profileId = progressItem.getProfileId()
  await progressPersistence.saveLocal(key, progressItem.getData(), {
    profileId,
  })
}

export async function clearProgress(progressItem: ProgressItem) {
  const key = progressItem.getKey()
  const profileId = progressItem.getProfileId()
  await progressPersistence.clearLocal(key, { profileId })
}

export function getProgressItemKeyFromConsumableContent(
  consumableContent: ConsumableContent,
) {
  return getProgressItemKey({
    contentId: consumableContent.content.id,
    contentType: consumableContent.content.type,
    podcastEpisode:
      consumableContent.type === "podcastEpisode"
        ? consumableContent.podcastEpisode.episode
        : undefined,
  })
}

export function getProgressItemKeyFromTrack(track: Track) {
  return getProgressItemKey({
    contentId: track.extra.contentId,
    contentType: track.extra.contentType,
    podcastEpisode:
      track.extra.contentType === "podcast"
        ? track.extra.podcastEpisodeNumber
        : undefined,
  })
}

export { getProgressItemKey }
export * from "./playable"
export * from "./readable"
